/* eslint-disable @tencent/i18next/no-literal-string */
import {
    AnalyzerResultDescriptionAndToSelectModel,
    AnalyzerResultType,
    IAnalyzerResult,
} from '@src/slide/application/service/slide-rating-and-improve/slide-content-analyzer.interface';
import i18n from '@i18next/i18n';
import { IPresentationModel } from '@src/slide/domain/ui-model/element-modeles/Interface/presentation-model.interface';
import {
    VTitleTemplate,
} from '@src/slide/application/service/slide-content-layout-analyzer/core/algorithm/data-structure/vertical-title-template';
import { ISlideModel } from '@src/slide/domain/ui-model/element-modeles/Interface/slide-model.interface';
import { ITitleResult } from '@src/slide/application/service/slide-content-layout-analyzer/core/algorithm/title-analyzer';
import {
    IRect,
    RectHorizontalAlignType,
    RectVerticalAlignType,
} from '@src/slide/infrastructure/module/independent/graphic/rect/rect';
import { isElementModel } from '@src/slide/domain/ui-model/elements/element-judge';
import { transaction as sdkTransaction } from '@src/slide/domain/slide-app';
import { IFrame } from '@src/slide/domain/ui-model/element-modeles/property/frame/frame.interface';
import { IPoint } from '@src/slide/infrastructure/module/independent/graphic/point/point.interface';
import {
    IElementBaseModel,
} from '@src/slide/domain/ui-model/element-modeles/Interface/base/element-base-model.interface';
import { IElementNode } from '@src/slide/application/service/slide-content-enumerator/enumerator-types.interface';
import { tdw } from '@src/slide/infrastructure/module/dependent/report/offline-data-report/tdw-report';
import {
    TDWActionType,
    TDWModuleType,
    TDWOperationType,
} from '@src/slide/infrastructure/module/dependent/report/offline-data-report/constant';
import { UiDataService } from '@src/slide/domain/ui-model/service/ui-data-service';
import { SelectionService } from '@src/slide/application/service/selection/selection-service';

export interface IShowContentChangeOption {
    originSlideList: ISlideModel[],
    toChangeSlideList: ISlideModel[],
    toChangeSlideIndexArray: Array<number>,
    slideWidth: number,
    slideHeight: number,
    toChangeElementRects: Map<number, IRect[]>,
    onConfirmModify: () => void,
    onCancel: () => void,
}

export class TitleAlignmentResult implements IAnalyzerResult {
    public analyzerName: string;
    public description: string;
    public resultType: AnalyzerResultType;
    private titleResult: ITitleResult | undefined;
    private vTitleTemplateErrorResult: Map<VTitleTemplate, Array<number>> = new Map<VTitleTemplate, Array<number>>();
    private toUpdateFrame: Map<IElementNode, Partial<IFrame>> = new Map();
    private presentation?: IPresentationModel;

    public constructor(
        analyzerName: string,
        description: string,
        private showContentChangeUIFunc?: (options: IShowContentChangeOption) => void,
    ) {
        this.analyzerName = analyzerName;
        this.description = description;
        this.reset();
    }

    public setShowContentChangeUI(showContentChangeUI: (options: IShowContentChangeOption) => void) {
        this.showContentChangeUIFunc = showContentChangeUI;
    }

    public getDescriptionWithSelect(): Array<AnalyzerResultDescriptionAndToSelectModel> | undefined {
        if (this.resultType === AnalyzerResultType.SUCCESS) return undefined;
        const result: Array<AnalyzerResultDescriptionAndToSelectModel> = [];
        let slideNumString = '';
        this.titleResult?.titleTemplateSimilarMap.forEach((indexArray) => {
            indexArray.forEach((num) => {
                if (slideNumString.length > 0) {
                    slideNumString += `, ${(num + 1)}`;
                } else {
                    slideNumString += `${(num + 1)}`;
                }
            });
        });
        result.push({
            selectable: false,
            description: i18n.t('以下页面的标题未对齐：{0}', { 0: slideNumString }),
        });
        return result;
    }

    public reset() {
        this.resultType = AnalyzerResultType.SUCCESS;
        this.toUpdateFrame = new Map();
        this.titleResult = undefined;
    }

    public generateResult(
        presentation: IPresentationModel,
        titleResult: ITitleResult,
    ) {
        this.presentation = presentation;
        this.titleResult = titleResult;
        this.vTitleTemplateErrorResult = new Map();
        this.titleResult.titleTemplateMap.forEach((areas, template) => {
            const sameTitleIndex = areas.map(area => area.slideIndex);
            const numbers = this.titleResult?.titleTemplateSimilarMap.get(template);
            // 没有相似的，说明都已经对齐了
            if (!numbers) return;
            this.resultType = AnalyzerResultType.ERROR;

            let templateIndexArray: Array<number> = [...sameTitleIndex];
            templateIndexArray = templateIndexArray.concat(numbers);
            templateIndexArray.sort((a, b) => a - b);
            this.vTitleTemplateErrorResult.set(template, templateIndexArray);
        });
    }

    public handleProblem(onHandled: () => void, onCancel: () => void) {
        if (!this.presentation) return;
        const cloneUiDataService = new UiDataService();
        const clonePresentation = this.presentation.clone();
        // @ts-ignore
        cloneUiDataService.presentation = clonePresentation;
        clonePresentation.selectionService = new SelectionService(cloneUiDataService);
        const slides = this.presentation?.children;
        const cloneSlides = slides?.map((slide, index) => {
            // 先clone下uiModel
            const cloneModel = slide.clone();
            cloneModel.parent = clonePresentation;
            return cloneModel as ISlideModel;
        });
        if (!slides || !cloneSlides) return null;
        clonePresentation.children = cloneSlides;
        // 再修改uiModel的frame，使得标题对齐
        this.changeCloneSlidesFrame(cloneSlides);

        const toChangeSlideIndexArray = this.getToChangeSlideIndexArray();

        const originList = toChangeSlideIndexArray.map(index => slides[index]);
        const previewList = toChangeSlideIndexArray.map(index => cloneSlides[index]);
        const previewRects = this.getPreviewRects(toChangeSlideIndexArray);

        this.showContentChangeUIFunc?.({
            originSlideList: originList,
            toChangeSlideList: previewList,
            toChangeSlideIndexArray,
            slideWidth: this.presentation?.props.slideWidth || 960,
            slideHeight: this.presentation?.props.slideHeight || 540,
            toChangeElementRects: previewRects,
            onConfirmModify: this.onConfirmModify.bind(this, onHandled),
            onCancel,
        });
    }

    private getToChangeSlideIndexArray(): Array<number> {
        const result: Array<number> = [];
        this.toUpdateFrame.forEach((frame, elementNode) => {
            const { slideIndex } = elementNode.slideNode;
            result.push(slideIndex);
        });
        return result.sort((a, b) => a - b);
    }

    private onConfirmModify(onHandled: () => void) {
        sdkTransaction(() => {
            this.toUpdateFrame.forEach((frame, elementNode) => {
                if (!isElementModel(elementNode.element)) return;
                elementNode.element.getFrame().updateFrameOnSlide({ frame });
            });
        });
        tdw({
            opername: TDWOperationType.DOC_PPT,
            module: TDWModuleType.AUTO_BEAUTIFY,
            action: TDWActionType.BEAUTIFY_TITLE_ALIGNMENT_SUCCESS,
        });
        onHandled();
    }

    private getPreviewRects(toChangeIndexArray: Array<number>): Map<number, IRect[]> {
        const rectsMap: Map<number, IRect[]> = new Map<number, IRect[]>();
        const slideIndexMap: Map<number, number> = new Map();
        toChangeIndexArray.forEach((slideIndex, currentIndex) => {
            slideIndexMap.set(slideIndex, currentIndex);
        });
        this.toUpdateFrame.forEach((frame, elementNode) => {
            const { slideIndex } = elementNode.slideNode;
            const currentFrame = {
                ...elementNode.element.getFrame().frameOnSlide(),
                ...frame,
            };
            const toChangeIndex = slideIndexMap.get(slideIndex);
            if (toChangeIndex === undefined) return;
            if (rectsMap.has(toChangeIndex)) {
                rectsMap.get(toChangeIndex)?.push(currentFrame);
            } else {
                rectsMap.set(toChangeIndex, [currentFrame]);
            }
        });
        return rectsMap;
    }

    private changeCloneSlidesFrame(cloneSlideModels: Array<ISlideModel>) {
        this.titleResult?.titleTemplateMap.forEach((areas, template) => {
            const similarSlideIndexArray = this.titleResult?.titleTemplateSimilarMap.get(template);
            similarSlideIndexArray?.forEach((similarSlideIndex) => {
                const cloneSlideModel = cloneSlideModels[similarSlideIndex];
                const templateBlocks = template.templateArea.node?.visualBlocks;
                const similarInfos = this.titleResult?.titleTemplateSimilarBlocksMap.get(similarSlideIndex);
                if (!similarInfos) return;
                // eslint-disable-next-line complexity
                templateBlocks?.forEach((templateBlock, index) => {
                    if (!similarInfos[index]) return;
                    const { similarBlock } = similarInfos[index];
                    if (!similarBlock.info) return;
                    const similarElement = similarBlock.info?.element;
                    const similarFrame = similarBlock.getFrame();

                    const templateRect = templateBlock.rect;
                    const similarRect = similarBlock.rect;
                    if (!similarFrame) return;
                    // left、top变化的内容
                    const toUpdateLeftTopOffset = this
                        .getToUpdateLeftTopOffset(template, templateRect, similarRect);
                    const toUpdateFrameOnSlide: Partial<IFrame> = {};

                    if (toUpdateLeftTopOffset.x !== undefined
                        && Math.abs(toUpdateLeftTopOffset.x) > 1e-3) {
                        toUpdateFrameOnSlide.left = similarFrame.left + toUpdateLeftTopOffset.x;
                    }
                    if (toUpdateLeftTopOffset.y !== undefined
                        && Math.abs(toUpdateLeftTopOffset.y - similarFrame.top) > 1e-3) {
                        toUpdateFrameOnSlide.top = similarFrame.top + toUpdateLeftTopOffset.y;
                    }
                    const toUpdateFrameOnSelf = similarElement?.getFrame().convertFrameOnSlideToSelf({
                        ...similarFrame,
                        ...toUpdateFrameOnSlide,
                    });
                    const cloneModelPath = similarBlock.info.path;
                    const cloneElement = this.getElementByPath(cloneSlideModel, cloneModelPath);
                    if (cloneElement) {
                        cloneElement.props.style.left = toUpdateFrameOnSelf?.left;
                        cloneElement.props.style.top = toUpdateFrameOnSelf?.top;
                        cloneElement.props.style.width = toUpdateFrameOnSelf?.width;
                        cloneElement.props.style.height = toUpdateFrameOnSelf?.height;
                        cloneElement.props.style.flipH = toUpdateFrameOnSelf?.flipH;
                        cloneElement.props.style.flipV = toUpdateFrameOnSelf?.flipV;
                        cloneElement.props.style.rotate = toUpdateFrameOnSelf?.rotate;
                        cloneElement.previewChange += 1;
                    }
                    this.toUpdateFrame.set(similarBlock.info, toUpdateFrameOnSlide);
                });
            });
        });
    }

    private getToUpdateLeftTopOffset(
        template: VTitleTemplate,
        templateFrame: IFrame,
        similarFrame: IFrame,
    ): Partial<IPoint> {
        const toUpdateLeftTopPoint: Partial<IPoint> = { };
        const leftOffset = this.getLeftOffset(template, templateFrame, similarFrame);
        const topOffset = this.getTopOffset(template, templateFrame, similarFrame);
        if (leftOffset !== 0) toUpdateLeftTopPoint.x = leftOffset;
        if (topOffset !== 0) toUpdateLeftTopPoint.y = topOffset;
        return toUpdateLeftTopPoint;
    }

    private getLeftOffset(
        template: VTitleTemplate,
        templateFrame: IFrame,
        similarFrame: IFrame,
    ): number {
        if (template.horAlignType === RectHorizontalAlignType.Same) {
            const left = templateFrame.left - similarFrame.left;
            const center = templateFrame.left + (templateFrame.width / 2)
                - (similarFrame.width / 2) - similarFrame.left;
            const right = templateFrame.left + templateFrame.width
                - similarFrame.width - similarFrame.left;
            const absLeft = Math.abs(left);
            const absCenter = Math.abs(center);
            const absRight = Math.abs(right);
            const absMin = Math.min(Math.abs(left), Math.abs(center), Math.abs(right));
            if (absLeft === absMin) {
                return left;
            }
            if (absCenter === absMin) {
                return center;
            }
            if (absRight === absMin) {
                return right;
            }
        } else if (template.horAlignType === RectHorizontalAlignType.Left) {
            return templateFrame.left - similarFrame.left;
        } else if (template.horAlignType === RectHorizontalAlignType.Center) {
            return templateFrame.left + (templateFrame.width / 2)
                - (similarFrame.width / 2) - similarFrame.left;
        } else if (template.horAlignType === RectHorizontalAlignType.Right) {
            return templateFrame.left + templateFrame.width
                - similarFrame.width - similarFrame.left;
        }
        return 0;
    }

    private getTopOffset(
        template: VTitleTemplate,
        templateFrame: IFrame,
        similarFrame: IFrame,
    ): number {
        if (template.verAlignType === RectVerticalAlignType.Same) {
            const top = templateFrame.top - similarFrame.top;
            const center = templateFrame.top + (templateFrame.height / 2)
                - (similarFrame.height / 2) - similarFrame.top;
            const bottom = templateFrame.top + templateFrame.height
                - similarFrame.height - similarFrame.top;
            const absTop = Math.abs(top);
            const absCenter = Math.abs(center);
            const absBottom = Math.abs(bottom);
            const absMin = Math.min(Math.abs(top), Math.abs(center), Math.abs(bottom));
            if (absTop === absMin) {
                return top;
            }
            if (absCenter === absMin) {
                return center;
            }
            if (absBottom === absMin) {
                return bottom;
            }
        } else if (template.verAlignType === RectVerticalAlignType.Top) {
            return templateFrame.top - similarFrame.top;
        } else if (template.verAlignType === RectVerticalAlignType.Center) {
            return templateFrame.top + (templateFrame.height / 2)
                - (similarFrame.height / 2) - similarFrame.top;
        } else if (template.verAlignType === RectVerticalAlignType.Bottom) {
            return templateFrame.top + templateFrame.height
                - similarFrame.height - similarFrame.top;
        }
        return 0;
    }

    private getElementByPath(slideModel: ISlideModel, path: Array<number>): IElementBaseModel | undefined {
        let current: ISlideModel | IElementBaseModel = slideModel;
        path.forEach((currentIndex) => {
            if (current.children && current.children.length > currentIndex) {
                current = current.children[currentIndex] as IElementBaseModel;
            }
        });
        if (current === slideModel) return undefined;
        return current as unknown as IElementBaseModel | undefined;
    }
}
